/**
 * Web i18n — i18next + react-i18next, per-namespace catalogs.
 *
 * The catalogs under ./locales are the mobile app's, unchanged: the two apps
 * are one product and must say the same words. Three things differ, and all
 * three are because this runs in a browser rather than in a binary:
 *
 *  1. The preferred locale comes from `navigator.languages`, not
 *     `expo-localization`.
 *  2. Direction is `dir` on <html> rather than `I18nManager.forceRTL`. This is
 *     strictly better: forceRTL needs a native reload before the view tree
 *     re-mirrors, so mobile has to ask the fan to restart, whereas `dir`
 *     re-mirrors on the next frame. `setAppLanguage` therefore always answers
 *     false ("no restart needed") and callers keep their existing branch.
 *  3. Arabic and French are LOADED ON DEMAND. The phone ships every catalog in
 *     its binary either way; here all three would be ~350 KB of JSON in the
 *     first download, and most sessions read one of them. English is bundled
 *     because it is also the fallback for a missing key in any language.
 *
 * Language resolution: explicit local choice (settingsStore, persisted) ->
 * server profile language (adopted once at sign-in) -> browser locale.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { queryClient } from '@/lib/queryClient';
import { useSettingsStore } from '@/features/settings/settingsStore';
import en from './locales/en';

export const SUPPORTED_LANGUAGES = ['en', 'ar', 'fr'] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const NAMESPACES = Object.keys(en) as (keyof typeof en)[];

const RTL_LANGUAGES: readonly AppLanguage[] = ['ar'];

export const isRTL = (lang: string): boolean => RTL_LANGUAGES.includes(lang as AppLanguage);

const isSupported = (lang: string): lang is AppLanguage =>
  (SUPPORTED_LANGUAGES as readonly string[]).includes(lang);

/** Best supported language from the browser's preferred locales; defaults to English. */
export function deviceLanguage(): AppLanguage {
  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const locale of preferred) {
    const code = locale?.split('-')[0]?.toLowerCase();
    if (code && isSupported(code)) {
      return code;
    }
  }
  return 'en';
}

/** Start-up language: the persisted explicit choice, else the browser locale. */
export function resolveInitialLanguage(persisted: string | null): AppLanguage {
  return persisted && isSupported(persisted) ? persisted : deviceLanguage();
}

void i18n.use(initReactI18next).init({
  resources: { en },
  // English until `ensureCatalog` has fetched whichever language wins; App.tsx
  // holds first paint until then, so this is never what the fan sees.
  lng: 'en',
  fallbackLng: 'en',
  ns: NAMESPACES,
  defaultNS: 'common',
  interpolation: { escapeValue: false }, // React escapes for us
  returnNull: false,
  ...(import.meta.env.DEV && {
    saveMissing: true,
    missingKeyHandler: (_lngs: readonly string[], ns: string, key: string) => {
      console.warn('[i18n] missing translation: ' + ns + ':' + key);
    },
  }),
});

const CATALOGS: Record<Exclude<AppLanguage, 'en'>, () => Promise<{ default: typeof en }>> = {
  ar: () => import('./locales/ar'),
  fr: () => import('./locales/fr'),
};

const loaded = new Set<string>(['en']);

/**
 * Make sure `lang`'s catalog is in i18next before anything renders with it.
 *
 * Idempotent, and safe to call concurrently: the second caller sees the language
 * already in `loaded` and returns. A failed chunk load falls through to English
 * rather than throwing — a fan on a bad connection should get the app in the
 * wrong language, not no app.
 */
export async function ensureCatalog(lang: string): Promise<void> {
  if (loaded.has(lang) || !isSupported(lang) || lang === 'en') return;
  try {
    const mod = await CATALOGS[lang]();
    for (const [namespace, resources] of Object.entries(mod.default)) {
      i18n.addResourceBundle(lang, namespace, resources, true, true);
    }
    loaded.add(lang);
  } catch {
    /* keep English rather than failing the language switch outright */
  }
}

/** Mirror the active language onto the document: `lang` for fonts and screen
 *  readers, `dir` for layout mirroring. */
function applyDirection(lang: string): boolean {
  const el = document.documentElement;
  el.lang = lang;
  el.dir = isRTL(lang) ? 'rtl' : 'ltr';
  return false; // the browser re-mirrors immediately — never a reload
}

/**
 * Switch the active language and align layout direction.
 * Always returns false (see the module header); the signature matches mobile's
 * so shared callers keep one code path.
 */
export async function setAppLanguage(lang: AppLanguage): Promise<boolean> {
  await ensureCatalog(lang);
  await i18n.changeLanguage(lang);
  // Server responses are rendered in the request language, so everything
  // cached under the old language is stale.
  void queryClient.invalidateQueries();
  return applyDirection(lang);
}

/**
 * Adopt the server profile language once, if no explicit choice was ever made
 * in this browser. Called after sign-in/bootstrap.
 */
export async function adoptProfileLanguage(lang: string): Promise<void> {
  const store = useSettingsStore.getState();
  if (store.language !== null || !isSupported(lang)) return;
  store.setLanguage(lang);
  if (lang !== i18n.language) {
    await ensureCatalog(lang);
    await i18n.changeLanguage(lang);
    applyDirection(lang);
    void queryClient.invalidateQueries();
  }
}

/** Align the document with the active language on first load. */
export function syncLayoutDirection(): void {
  applyDirection(i18n.language);
}

export default i18n;
